import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import React from 'react';

import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import UnoButton from '../../../../../utils/UnoButton';

const useStyles = makeStyles({
    //Mesma moldura da TrademateDocumentRow, para as duas seções lerem como uma lista só
    row: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        border: 'solid 1px ' + unoTheme.borderInputColor,
        borderRadius: '10px',
        padding: '10px 16px',
        marginBottom: '8px',
    },
    statusField: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
    },
    infoField: {
        flex: 1,
        minWidth: 0,
    },
    name: {
        fontSize: '13px',
        fontWeight: 600,
        color: unoTheme.defaultTextColor,
    },
    statePending: {
        fontSize: '11px',
        marginTop: '2px',
        color: unoTheme.warningColorStrong,
    },
    file: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '2px',
        minWidth: 0,
    },
    fileName: {
        fontSize: '11px',
        color: unoTheme.greenMoney,
        //O nome do arquivo pode ser longo; corta em vez de empurrar os botões
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileIcon: {
        fontSize: '14px',
        color: unoTheme.greenMoney,
    },
    //Remover fica discreto: cinza e pequeno, só fica vermelho no hover. O `type='error'` do
    //UnoButton é um botão vermelho cheio, que pesava demais repetido a cada arquivo da lista.
    //!important porque o myIconButton do UnoButton já define padding.
    removeFile: {
        padding: '4px !important',
        color: unoTheme.neutralIconColor,

        '& .MuiSvgIcon-root': {
            fontSize: '18px',
        },
        '&:hover': {
            color: unoTheme.errorColor,
            backgroundColor: 'transparent',
        },
    },
    actionsField: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
});

/**
 * Uma linha de documento complementar na etapa 2: Lei municipal, RG, CPF, Publicação no DO.
 *
 * Não é uma variação da TrademateDocumentRow porque aquela é toda sobre gerar e assinar, e aqui não
 * há nada disso: o RPPS só anexa. Cada arquivo anexado aparece embaixo do nome com seus próprios
 * baixar/remover — RG e CPF podem ter vários (frente e verso).
 *
 * Como na outra linha, o `<input type="file">` vive no pai e a prop `document` sombreia o global.
 *
 * @param {{
 *   document: {
 *     name: string, multiple_files: boolean,
 *     files: {id: number, file_name: string}[],
 *   },
 *   canEdit: boolean,
 *   onUpload: () => void,
 *   onDownloadFile: (file: object) => void,
 *   onRemoveFile: (file: object) => void,
 * }} props
 */
export default function TrademateComplementaryDocumentRow(props) {

    const { document, canEdit, onUpload, onDownloadFile, onRemoveFile } = props;

    const classes = useStyles();

    const files = document.files ? document.files : [];
    const hasFiles = files.length > 0;

    //Em documento de arquivo único, anexar de novo troca o que está lá; em RG/CPF, soma
    const uploadLabel = !hasFiles
        ? 'Anexar'
        : (document.multiple_files ? 'Adicionar arquivo' : 'Substituir');

    return (
        <div className={classes.row}>

            <div className={classes.statusField}>
                {hasFiles
                    ? <CheckCircleIcon style={{ color: unoTheme.successColor }} />
                    : <RadioButtonUncheckedIcon style={{ color: unoTheme.neutralIconColor }} />}
            </div>

            <div className={classes.infoField}>

                <Typography align="left" className={classes.name}>{document.name}</Typography>

                {!hasFiles && (
                    <Typography align="left" className={classes.statePending}>
                        {document.multiple_files
                            ? 'Anexo pendente · aceita mais de um arquivo (ex.: frente e verso)'
                            : 'Anexo pendente'}
                    </Typography>
                )}

                {files.map((file) => (
                    <div key={'file-' + file.id} className={classes.file}>
                        <AttachFileIcon className={classes.fileIcon} />
                        <Typography align="left" className={classes.fileName}>
                            {file.file_name ? file.file_name : '-'}
                        </Typography>
                        <UnoButton
                            isIconButton
                            removeDefaultLeftMargin
                            size='small'
                            type='successClean'
                            title='Baixar'
                            myIcon={<GetAppIcon />}
                            onClick={() => onDownloadFile(file)}
                        />
                        {canEdit && (
                            <UnoButton
                                isIconButton
                                removeDefaultLeftMargin
                                size='small'
                                className={classes.removeFile}
                                title='Remover'
                                myIcon={<DeleteIcon />}
                                onClick={() => onRemoveFile(file)}
                            />
                        )}
                    </div>
                ))}

            </div>

            <div className={classes.actionsField}>
                {canEdit && (
                    <UnoButton
                        size='small'
                        type={hasFiles ? 'primaryClean' : 'primary'}
                        startIcon={<PublishIcon />}
                        onClick={onUpload}>
                        {uploadLabel}
                    </UnoButton>
                )}
            </div>

        </div>
    );
}
